// src/composables/useProfileGeneral.js
import { ref, computed, watch } from '@vue/composition-api'
import store from '@/store'
import { kpEndpoint, kpRequest } from '@/axios'
import {
 dataURLtoFile, toBase64, getLinkWithoutQueryParams, getEntityPayloadForGeneralSection,
} from '@/common-utils'
import { formatEntityData } from '@/utils/formatEntityData'
import analytics from '@/@core/utils/analytics'
import constants, { IMAGE_COMPRESSER, USER_ROLES_LABEL, FILE_TYPES } from '@/constants'
import { FETCH_PROFILE, UPDATE_ENTITY } from '@/store/modules/auth.module'
import { UPLOAD_DOCUMENTS } from '@/store/modules/common.module'
import { apiToastSuccess } from '@/@core/utils/toast'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import Compress from 'compress.js'

const { STORE_TYPE } = c

export function useProfileGeneral() {
  // --- Section B: Reactive State ---
  const generalLocal = ref({})
  const initialFirstName = ref('')
  const initialLastName = ref('')
  const logoFile = ref(null)
  const uncroppedLogoFile = ref(null)
  const logoImg = ref(undefined)
  const bannerImgs = ref([])
  const saving = ref(false)
  const croppedImg = ref(null)
  const isCropping = ref(false)
  const fullPhoneNumber = ref('')
  const isValidPhoneNumber = ref(false)

  // --- Section C: Computed ---
  const entityName = computed(() => {
    const entityType = store.getters.entityType
    return `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} Name`
  })
  const isRetailer = computed(() => store.getters.isRetailer)
  const isBrand = computed(() => store.getters.isBrand)
  const isNotEntityAdmin = computed(() => !store.getters.isEntityAdmin)
  const isNotValidPhoneNumber = computed(() => generalLocal.value?.phone && !isValidPhoneNumber.value)
  const haveUserDetailsChanged = computed(() => (
      generalLocal.value.firstName !== initialFirstName.value
      || generalLocal.value.lastName !== initialLastName.value
    ))

  // --- Section D: BUSINESS methods ---
  function buildDefault(generalData) {
    return JSON.parse(JSON.stringify(generalData || {}))
  }

  function initGeneral(generalData) {
    generalLocal.value = buildDefault(generalData)
    initialFirstName.value = generalLocal.value.firstName || ''
    initialLastName.value = generalLocal.value.lastName || ''
    logoImg.value = generalLocal.value.logo
      ? `${generalLocal.value.logo}?t=${Date.now()}`
      : undefined
    bannerImgs.value = generalLocal.value.banner || []
    fullPhoneNumber.value = generalLocal.value.phone
      ? `${generalLocal.value.countryCode || ''}${generalLocal.value.phone}`
      : ''
    // Phone from API is already validated — mark as valid so save button is not blocked
    if (generalLocal.value.phone) {
      isValidPhoneNumber.value = true
    } else {
      isValidPhoneNumber.value = false
    }
  }

  async function uploadLogo() {
    if (logoFile.value instanceof File) {
      const res = await store.dispatch(UPLOAD_DOCUMENTS, {
        fileType: FILE_TYPES.LOGO,
        file: logoFile.value,
      })
      generalLocal.value.logo = res
      logoImg.value = `${res}?t=${Date.now()}`
      apiToastSuccess('Your file(s) are uploaded')
    }
  }

  async function getGeneralPayload(resetLogo = false, resetBanner = false) {
    saving.value = true
    if (!resetLogo) await uploadLogo()

    const payload = { ...generalLocal.value }

    if (resetLogo) {
      payload.logo = ''
    } else if (!(logoFile.value instanceof File) || !logoFile.value) {
      delete payload.logo
    }
    if (resetBanner) {
      payload.banner = []
    } else {
      payload.banner = (generalLocal.value.banner || []).map(img => getLinkWithoutQueryParams(img))
    }
    if (isBrand.value) {
      delete payload.storeType
    }
    delete payload.email
    return payload
  }

  async function getApiCalls(payload) {
    const calls = []
    if (haveUserDetailsChanged.value) {
      calls.push(
        kpRequest({
          ...kpEndpoint.profile.general,
          payload,
        }),
      )
    }
    if (!isNotEntityAdmin.value) {
      const entityData = formatEntityData({ ...store.getters.entityData })
      const p = getEntityPayloadForGeneralSection(payload, entityData)
      calls.push(store.dispatch(UPDATE_ENTITY, p))
    }
    return calls
  }

  async function saveGeneralSettings(resetLogo = false, resetBanner = false) {
    try {
      const payload = await getGeneralPayload(resetLogo, resetBanner)
      const apiCalls = await getApiCalls(payload)
      const results = await Promise.all(apiCalls)
      const successMessage = results[0]?.data?.message || 'Profile updated successfully.'
      apiToastSuccess(successMessage)
      analytics.track(constants.TRACKS.ACTIONS.SAVE_PROFILE_SETTINGS_GENERAL)
      await store.dispatch(FETCH_PROFILE)
      return { success: true, payload }
    } catch {
      return { success: false }
    } finally {
      saving.value = false
    }
  }

  async function cropLogo(cropperRef) {
    isCropping.value = true
    try {
      const { canvas } = cropperRef.getResult()
      const logoDataUrl = canvas.toDataURL()
      let file = dataURLtoFile(logoDataUrl, 'logo.png')
      if (file.size / 1024 > 1024) {
        const compress = new Compress()
        const data = await compress.compress([file], IMAGE_COMPRESSER)
        file = dataURLtoFile(data[0].prefix + data[0].data, 'logo.png')
      }
      logoImg.value = logoDataUrl
      logoFile.value = file
      const result = await saveGeneralSettings()
      return result
    } finally {
      isCropping.value = false
    }
  }

  function resetLogo() {
    logoFile.value = null
    logoImg.value = null
    generalLocal.value = { ...generalLocal.value, logo: '' }
    return saveGeneralSettings(true)
  }

  function resetBanners() {
    return saveGeneralSettings(false, true)
  }

  // --- Section D: HYBRID methods (business part only) ---
  function onInputPhoneNumber(number, phoneObject) {
    generalLocal.value.phone = phoneObject?.nationalNumber || ''
    generalLocal.value.countryCode = phoneObject?.countryCallingCode
      ? `+${phoneObject.countryCallingCode}`
      : ''
    fullPhoneNumber.value = number
    isValidPhoneNumber.value = phoneObject?.valid || false
  }

  function updateBannerImgs(newBanners) {
    bannerImgs.value = newBanners
    generalLocal.value.banner = newBanners
  }

  function resetForm(generalData) {
    initGeneral(generalData)
  }

  // --- Section H: Watcher setup ---
  function setupLogoWatcher(callback) {
    // Watcher for uncroppedLogoFile — converts to Base64 and triggers UI callback
    watch(
      uncroppedLogoFile,
      async newFile => {
        if (newFile) {
          croppedImg.value = await toBase64(newFile)
          if (callback) callback()
        }
      },
      { immediate: true },
    )
  }

  return {
    // State
    generalLocal,
    initialFirstName,
    initialLastName,
    logoFile,
    uncroppedLogoFile,
    logoImg,
    bannerImgs,
    saving,
    croppedImg,
    isCropping,
    fullPhoneNumber,
    isValidPhoneNumber,
    STORE_TYPE,
    USER_ROLES_LABEL,
    // Computed
    entityName,
    isRetailer,
    isBrand,
    isNotEntityAdmin,
    isNotValidPhoneNumber,
    haveUserDetailsChanged,
    // Methods
    initGeneral,
    buildDefault,
    uploadLogo,
    getGeneralPayload,
    getApiCalls,
    saveGeneralSettings,
    cropLogo,
    resetLogo,
    resetBanners,
    onInputPhoneNumber,
    updateBannerImgs,
    resetForm,
    setupLogoWatcher,
  }
}
